"use client";

import React, { useEffect, useRef } from "react";

// How much slower each layer moves relative to normal scroll speed.
// 0 = fixed in place, 1 = scrolls at normal speed.
const BG_SPEED = 0.4;
const CONTENT_SPEED = 0.15;

const ParallBengaluru = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const bg = bgRef.current;
    const content = contentRef.current;
    if (!section || !bg || !content) return;

    let ticking = false;

    const updateParallax = () => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.bottom < 0 || rect.top > viewportHeight) {
        ticking = false;
        return;
      }

      const scrolled = -rect.top;
      const bgOffset = scrolled * BG_SPEED;
      const contentOffset = scrolled * CONTENT_SPEED;

      bg.style.transform = `translate3d(0, ${bgOffset}px, 0)`;
      content.style.transform = `translate3d(0, ${contentOffset}px, 0)`;

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateParallax();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <>
      {/* Desktop: JS-driven parallax, background and content drift at different rates */}
      <section
        ref={sectionRef}
        className="hidden md:flex relative w-full h-[90vh] items-start justify-center overflow-hidden pt-24 bg-white"
      >
        <div
          ref={bgRef}
          className="absolute left-0 top-[-20%] h-[125%] w-full bg-cover bg-center bg-no-repeat [will-change:transform]"
          style={{
            backgroundImage: `url(https://ik.imagekit.io/mileseducation/miles_website/City/Bengaluru/ParalexBengaluru1.webp`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/10" />
        <div
          ref={contentRef}
          className="relative z-[2] mx-auto max-w-[760px] px-4 text-center [will-change:transform] pt-10"
        >
          <h2 className="mb-4 text-[34px] font-bold leading-tight">
            <span className="text-[#061A2F]">Why </span>
            <span className="text-[#39F]">CPA Makes Sense in Bangalore</span>
          </h2>
          <p className="mx-auto max-w-[700px] text-base leading-relaxed text-[#333]">
            Bangalore is home to roughly a third of India’s Global Capability
            Centres, more than any other city in the country. That means a
            steady pipeline of finance roles built around US clients, global
            reporting standards, and cross-border audit and tax work. A CPA is
            what lets you move from supporting that work to leading it.
          </p>
        </div>
      </section>

      {/* Mobile: static card, image below the copy, no parallax */}
      <section className="md:hidden w-full px-4 pt-6 bg-[#f3f3f3]">
        <div className="w-full overflow-hidden rounded-[28px] bg-white shadow-md">
          <div className="px-4 pt-8 pb-2 text-center">
            <h2 className="mb-4 text-2xl font-bold leading-tight">
              <span className="text-[#061A2F]">Why </span>
              <span className="text-[#39F]">
                CPA Makes Sense
                <br />
                in Bangalore
              </span>
            </h2>
            <p className="mx-auto max-w-[300px] text-sm font-medium leading-tight text-[#000]">
              Bangalore is home to roughly a third of India&apos;s Global
              Capability Centres, more than any other city in the country. That
              means a steady pipeline of finance roles built around US clients,
              global reporting standards, and cross-border audit and tax work. A
              CPA is what lets you move from supporting that work to leading it.
            </p>
          </div>
          <img
            src="https://ik.imagekit.io/mileseducation/miles_website/City/Bengaluru/bengaluruParalexmob1.webp"
            alt="Why CPA makes sense in Bangalore"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>
    </>
  );
};

export default ParallBengaluru;
